import React, { useState } from 'react';

import { Select } from '@aline/common';
import { useApolloClient } from '@apollo/client/react';
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import type { GridColumnHeaderParams } from '@mui/x-data-grid-premium';
import { useGridApiContext } from '@mui/x-data-grid-premium';
import { useAccessGuard } from 'src/components/auth';
import { CrossIconCircleButton } from 'src/components/generic';
import { AddExistingPropertyIcon } from 'src/components/icons/AddExistingPropertyIcon';
import { AddNewPropertyIcon } from 'src/components/icons/AddNewPropertyIcon';
import type { GridReportColOption } from 'src/components/reports/helpers';
import { createReportGridColDef, showColumnAtPosition } from 'src/components/reports/helpers';
import { ReportColumnsPicker } from 'src/components/reports/report-detail/ReportColumnsPicker';
import type { ReportGridRow } from 'src/components/reports/types';
import { Apollo } from 'src/gql/generated';
import { reportTypename } from 'src/helpers';
import { useCreateExtractionQuestion } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  listItemIcon: {
    marginRight: theme.spacing(1),
    minWidth: 'unset',
  },
}));

export function AddReportColumn({
  colDef,
  reportId,
}: GridColumnHeaderParams<ReportGridRow, ReportGridRow> & { reportId: UUID }): JSX.Element {
  const { classes } = useStyles();
  const apiRef = useGridApiContext();
  const client = useApolloClient();
  const anchorEl = apiRef.current.getColumnHeaderElement(colDef.field);
  const [openPicker, setOpenPicker] = useState<'new-property' | 'existing-property' | null>(null);
  const { createExtractionQuestion, loading: creatingQuestion } = useCreateExtractionQuestion();
  const handleOpenPicker = useAccessGuard({
    callback: () => setOpenPicker('new-property'),
    minRole: Apollo.OrgRole.Basic,
  });

  return (
    <>
      <CrossIconCircleButton
        dataCy={Select.report.addColumnButton}
        disabled={creatingQuestion}
        onClick={handleOpenPicker}
      />
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        open={openPicker === 'new-property'}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        onClose={() => setOpenPicker(null)}
      >
        <MenuItem
          data-cy={Select.report.addStandardColumnButton}
          onClick={async () => {
            setOpenPicker(null);

            const visibleColumns = apiRef.current.getVisibleColumns();
            const { data } = await createExtractionQuestion({
              variables: {
                input: {
                  category: 'General',
                  questionText: '',
                  title: 'New property',
                  type: Apollo.ExtractionQuestionType.Text,
                },
              },
            });
            if (!data) return;

            const colOption: GridReportColOption = {
              __typename: 'ExtractionReportColumn',
              dataType: Apollo.ReportColumnDataType.String,
              displayName: data.createExtractionQuestion.title,
              isGroupable: true,
              questionId: data.createExtractionQuestion.id,
            };
            const newColDef = createReportGridColDef(colOption);

            client.cache.modify({
              fields: {
                columnOptions: (existingColumnOptions = []) => {
                  return [...existingColumnOptions, colOption];
                },
              },
              id: client.cache.identify({
                __typename: reportTypename,
                id: reportId,
              }),
            });
            apiRef.current.updateColumns([newColDef]);

            const setupColumn = () => {
              const column = apiRef.current.getColumn(newColDef.field);
              if (!column) {
                // Column not ready yet, try again on next frame.
                requestAnimationFrame(setupColumn);
                return;
              }

              // Column is ready - set visibility and position, then scroll and show menu.
              const targetIndex = visibleColumns.length;
              showColumnAtPosition(apiRef, newColDef.field, targetIndex);
              requestAnimationFrame(() => {
                const actualIndex = apiRef.current.getColumnIndex(newColDef.field);
                apiRef.current?.scrollToIndexes({ colIndex: actualIndex });
                // Extra frame to ensure scroll is complete before showing menu.
                requestAnimationFrame(() => {
                  apiRef.current?.showColumnMenu(newColDef.field);
                });
              });
            };
            requestAnimationFrame(setupColumn);
          }}
        >
          <ListItemIcon className={classes.listItemIcon}>
            <AddNewPropertyIcon />
          </ListItemIcon>
          <ListItemText>Create new property</ListItemText>
        </MenuItem>
        <MenuItem
          data-cy={Select.report.addStandardColumnButton}
          onClick={() => setOpenPicker('existing-property')}
        >
          <ListItemIcon className={classes.listItemIcon}>
            <AddExistingPropertyIcon />
          </ListItemIcon>
          <ListItemText>Add existing property</ListItemText>
        </MenuItem>
      </Menu>
      <ReportColumnsPicker
        anchorEl={anchorEl}
        open={openPicker === 'existing-property'}
        onClose={() => setOpenPicker(null)}
      />
    </>
  );
}
